import React from 'react';

import { Bearing } from '@paragon/contract-library';
import { Direction2D, distanceEquals, Point2D } from '@paragon/geometry-library';
import { BearingVisualData } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { setBearingWidth } from '../../store/actions/editBearingActions';
import { calculateActiveComponentMembersMinMax } from '../../store/selectors/boundsSelectors';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { DistanceDimension } from './DistanceDimension';
import { EditableDistanceDimension } from './EditableDistanceDimension';

export const BearingDimensions = (props: { bearingToEdit?: Bearing; visualData: BearingVisualData | null }) => {
  const { bearingToEdit, visualData } = props;
  const dispatch = useAppDispatch();
  const activeComponentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const componentBounds = useAppSelector(calculateActiveComponentMembersMinMax);
  if (!activeComponentDesignGuid || !componentBounds || !visualData) return null;
  const { width, height, position, normal } = visualData;
  if (normal.equals(Direction2D.up)) {
    const widthLeft = new Point2D(-width / 2, -(height + 1)).add(position);
    const widthRight = new Point2D(+width / 2, -(height + 1)).add(position);
    const widthDimension = bearingToEdit ? (
      <EditableDistanceDimension
        start={widthLeft}
        end={widthRight}
        textFloat={-2.5}
        distance={widthLeft.distanceTo(widthRight)}
        showEndpointMarks
        onChange={(width) => dispatch(setBearingWidth(bearingToEdit, activeComponentDesignGuid, width))}
      />
    ) : null;

    const sideDimensionY = position.y - height / 2;

    const rightDimensionLeft = new Point2D(widthRight.x, sideDimensionY);
    const rightDimensionRight = new Point2D(componentBounds.maxX, sideDimensionY);
    const rightDimension = <BearingDimension start={rightDimensionLeft} end={rightDimensionRight} />;

    const leftDimensionLeft = new Point2D(componentBounds.minX, sideDimensionY);
    const leftDimensionRight = new Point2D(widthLeft.x, sideDimensionY);
    const leftDimension = <BearingDimension start={leftDimensionLeft} end={leftDimensionRight} />;

    return (
      <>
        {leftDimension}
        {widthDimension}
        {rightDimension}
      </>
    );
  }

  const normalIsLeft = normal.equals(Direction2D.left);
  if (normalIsLeft || normal.equals(Direction2D.right)) {
    const flip = normalIsLeft ? -1 : 1;
    const widthBottom = new Point2D((height + 1) * -flip, -width / 2).add(position);
    const widthTop = new Point2D((height + 1) * -flip, width / 2).add(position);
    const widthDimension = bearingToEdit ? (
      <EditableDistanceDimension
        start={widthBottom}
        end={widthTop}
        distance={widthBottom.distanceTo(widthTop)}
        showEndpointMarks
        onChange={(width) => dispatch(setBearingWidth(bearingToEdit, activeComponentDesignGuid, width))}
      />
    ) : null;

    const sideDimensionX = position.x - (height / 2) * flip;

    const bottomDimensionBottom = new Point2D(sideDimensionX, 0);
    const bottomDimensionTop = new Point2D(sideDimensionX, widthBottom.y);
    const bottomDimension = <BearingDimension start={bottomDimensionBottom} end={bottomDimensionTop} />;

    return (
      <>
        {bottomDimension}
        {widthDimension}
      </>
    );
  }

  return null;
};

const BearingDimension = ({ start, end }: { start: Point2D; end: Point2D }) => {
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  const distance = start.distanceTo(end);
  if (distanceEquals(distance, 0)) return null;
  return (
    <DistanceDimension
      segment={{ start, end }}
      textFloat={-2}
      showEndpointMarks
      distanceFormatter={distanceFormatter}
      labelData={{ fontSize: 2, firstNumber: distance, applyAntiZoomFactor: true }}
    />
  );
};
